import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Loader2, Search, Percent, ChevronDown, User } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Set Commissions — simplified per-operator commission editor.
 *
 * MASTER_ADMIN picks any AGENT or MASTER_AGENT; MASTER_AGENT picks one of
 * their AGENTS. Table shows one row per operator (Digicel Haiti, Natcom Haiti,
 * MonCash, NatCash, Orange DR, Claro DR, VIVA DR, etc.). The operator commission
 * is entered manually — no Ding API percentages, no hierarchy % validation.
 *
 * Save via a per-row "Update" button matching the user's reference design.
 */
export default function SetCommissions() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const targetUserId = params.get("user_id") || "";
  // Optional role filter — restricts the picker to AGENT or MASTER_AGENT only.
  // Set from sidebar links to prevent Agents/Master Agents from mixing.
  const roleFilter = params.get("role") || "";

  const [candidates, setCandidates] = useState([]);
  const [target, setTarget] = useState(null);
  const [rows, setRows] = useState([]);
  const [requesterRole, setRequesterRole] = useState(user?.role);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [country, setCountry] = useState("");
  const [savingKey, setSavingKey] = useState(null);
  const [draft, setDraft] = useState({});

  // Load candidates. Scope by roleFilter when present, else full available set.
  const userRole = user?.role;
  useEffect(() => {
    (async () => {
      let list = [];
      if (userRole === "MASTER_ADMIN") {
        try {
          if (roleFilter === "MASTER_AGENT") {
            const mRes = await api.get("/master-agents");
            list = Array.isArray(mRes.data) ? mRes.data : mRes.data.items || [];
          } else if (roleFilter === "AGENT") {
            const aRes = await api.get("/agents");
            list = Array.isArray(aRes.data) ? aRes.data : aRes.data.items || [];
          } else {
            const [mRes, aRes] = await Promise.all([
              api.get("/master-agents"),
              api.get("/agents"),
            ]);
            const masters = Array.isArray(mRes.data) ? mRes.data : mRes.data.items || [];
            const agents = Array.isArray(aRes.data) ? aRes.data : aRes.data.items || [];
            list = [...masters, ...agents];
          }
        } catch { list = []; }
      } else {
        // MASTER_AGENT: always agents only (their own subagents).
        try {
          const r = await api.get("/agents");
          const items = Array.isArray(r.data) ? r.data : r.data.items || [];
          list = items;
        } catch { list = []; }
      }
      setCandidates(list.filter((c) => c.active !== false));
    })();
  }, [userRole, roleFilter]);

  // If the role filter changes but a target of the wrong role is selected,
  // reset the selection so we don't load a mismatched target.
  useEffect(() => {
    if (!targetUserId || !roleFilter) return;
    const t = candidates.find((c) => c.id === targetUserId);
    if (t && t.role !== roleFilter) {
      const p = new URLSearchParams(params);
      p.delete("user_id");
      setParams(p);
    }
  }, [roleFilter, candidates, targetUserId, params, setParams]);

  // Load operator-commissions when the target changes / filters change.
  useEffect(() => {
    if (!targetUserId) { setTarget(null); setRows([]); return; }
    setLoading(true);
    api.get(`/commissions/operators/for-user/${targetUserId}`, {
      params: { country: country || undefined, search: q || undefined },
    })
      .then((r) => {
        setTarget(r.data.target);
        setRows(r.data.items || []);
        setRequesterRole(r.data.requester_role || userRole);
        setDraft({});
      })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail) || "Failed to load commissions"))
      .finally(() => setLoading(false));
  }, [targetUserId, country, q, userRole]);

  const countries = useMemo(() => {
    const s = new Set();
    for (const r of rows) if (r.country_code) s.add(r.country_code);
    return [...s].sort();
  }, [rows]);

  const pickTarget = (id) => {
    const p = new URLSearchParams(params);
    p.set("user_id", id);
    setParams(p);
  };

  const filteredCandidates = useMemo(() => {
    return candidates; // Selector below has its own search
  }, [candidates]);

  const save = async (row) => {
    const raw = draft[row.operator_id];
    const pct = Number(raw);
    if (!Number.isFinite(pct) || pct < 0 || pct > 100) {
      toast.error("Enter a percentage between 0 and 100");
      return;
    }
    // Client-side hierarchy check — matches the backend rule for MASTER_AGENT.
    if (requesterRole === "MASTER_AGENT" && pct > (row.parent_pct ?? 0) + 1e-9) {
      toast.error(`You can allocate up to ${row.parent_pct}% — that is your own commission on this operator.`);
      return;
    }
    setSavingKey(row.operator_id);
    try {
      await api.put(`/commissions/operators/for-user/${targetUserId}`, {
        operator_id: row.operator_id,
        pct,
      });
      setRows((rs) => rs.map((r) => r.operator_id === row.operator_id ? { ...r, pct } : r));
      setDraft((d) => { const n = { ...d }; delete n[row.operator_id]; return n; });
      toast.success(t("commissions.saved"));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || t("ui.failed_generic"));
    } finally {
      setSavingKey(null);
    }
  };

  const displayValue = (row) => draft[row.operator_id] !== undefined
    ? draft[row.operator_id]
    : String(row.pct ?? 0);

  return (
    <div className="space-y-6" data-testid="set-commissions-page">
      <div>
        <h1 className="text-3xl sm:text-4xl font-bold text-slate-900" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
          {roleFilter === "MASTER_AGENT" ? t("commissions.title_master")
           : roleFilter === "AGENT" ? t("commissions.title_agent")
           : t("commissions.title")}
        </h1>
        <p className="text-slate-500 mt-1 max-w-3xl">
          {roleFilter === "MASTER_AGENT"
            ? t("commissions.subtitle_master")
            : roleFilter === "AGENT"
            ? t("commissions.subtitle_agent")
            : t("commissions.subtitle")}
        </p>
      </div>

      {/* Target picker (dropdown-style, matches the reference design) */}
      <div className="bg-white rounded-md border border-slate-200 shadow-sm p-4 max-w-3xl">
        <label className="block text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-1.5">
          {t("commissions.pick_agent")}
        </label>
        {candidates.length === 0 ? (
          <div className="text-sm text-slate-500 py-2">{t("commissions.no_agents")}</div>
        ) : (
          <div className="relative">
            <User className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <select
              data-testid="commissions-target-select"
              value={targetUserId}
              onChange={(e) => pickTarget(e.target.value)}
              className="h-11 w-full rounded-lg border border-slate-300 pl-9 pr-8 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#0F62FE] appearance-none"
            >
              <option value="">— {t("commissions.pick_agent")} —</option>
              {filteredCandidates.map((c) => (
                <option key={c.id} value={c.id} data-testid={`commissions-option-${c.username}`}>
                  {(c.agent_code || c.username)} — {c.name || c.username} ({c.role?.replace("_", " ")})
                </option>
              ))}
            </select>
            <ChevronDown className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          </div>
        )}
      </div>

      {targetUserId && (
        <>
          {/* Filters + search */}
          <div className="bg-white rounded-md border border-slate-200 shadow-sm p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                data-testid="commissions-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={t("commissions.search_products")}
                className="h-10 w-full rounded-lg border border-slate-300 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F62FE]"
              />
            </div>
            <div className="relative">
              <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">
                {t("commissions.country")}
              </label>
              <select
                data-testid="commissions-country"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                className="h-10 w-full rounded-lg border border-slate-300 pl-3 pr-8 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#0F62FE] appearance-none"
              >
                <option value="">{t("commissions.all")}</option>
                {countries.map((c) => (<option key={c} value={c}>{c}</option>))}
              </select>
              <ChevronDown className="w-4 h-4 absolute right-3 bottom-3 text-slate-400 pointer-events-none" />
            </div>
          </div>

          {/* Table */}
          <div className="bg-white rounded-md border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between text-xs text-slate-500">
              <div>{t("commissions.showing", { count: rows.length })}</div>
              {target && (
                <div className="text-slate-700 font-semibold" data-testid="commissions-target-badge">
                  {target.name || target.username} · {target.role?.replace("_", " ")}
                </div>
              )}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="commissions-table">
                <thead>
                  <tr className="bg-slate-50 text-[11px] uppercase tracking-widest text-slate-500">
                    <th className="px-5 py-3 text-left w-16">{t("my_commissions.col_id")}</th>
                    <th className="px-5 py-3 text-left">{t("commissions.col_product")}</th>
                    {requesterRole === "MASTER_AGENT" && (
                      <th className="px-5 py-3 text-left w-40">{t("commissions.col_master_commission")}</th>
                    )}
                    <th className="px-5 py-3 text-left w-56">{t("commissions.col_agent_commission")}</th>
                    <th className="px-5 py-3 text-left w-32">{t("common.actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={requesterRole === "MASTER_AGENT" ? 5 : 4} className="px-5 py-8 text-center text-slate-500">
                      <Loader2 className="w-5 h-5 animate-spin inline mr-2" />{t("common.loading")}
                    </td></tr>
                  ) : rows.length === 0 ? (
                    <tr><td colSpan={requesterRole === "MASTER_AGENT" ? 5 : 4} className="px-5 py-8 text-center text-slate-500">{t("commissions.empty")}</td></tr>
                  ) : rows.map((r) => (
                    <tr key={r.operator_id} className="border-b border-slate-100" data-testid={`commissions-row-${r.operator_id}`}>
                      <td className="px-5 py-3 text-slate-500 text-xs">{r.row_number}</td>
                      <td className="px-5 py-3">
                        <div className="font-medium text-slate-900">{r.operator_name}</div>
                        <div className="text-[11px] text-slate-500 flex items-center gap-2">
                          {r.country_code || "—"}
                          {r.kind === "cash" && (
                            <span className="inline-flex px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 font-semibold text-[9px] uppercase tracking-wider">
                              {t("my_commissions.cash_badge")}
                            </span>
                          )}
                        </div>
                      </td>
                      {requesterRole === "MASTER_AGENT" && (
                        <td className="px-5 py-3" data-testid={`commissions-master-pct-${r.operator_id}`}>
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-violet-50 text-violet-700 font-semibold text-xs border border-violet-200">
                            {Number(r.parent_pct || 0).toFixed(2)}%
                          </span>
                          <div className="text-[10px] text-slate-500 mt-1">{t("commissions.you_keep", { pct: Math.max((r.parent_pct || 0) - (draft[r.operator_id] !== undefined ? Number(draft[r.operator_id] || 0) : (r.pct || 0)), 0).toFixed(2) })}</div>
                        </td>
                      )}
                      <td className="px-5 py-3">
                        <div className="relative w-40">
                          <input
                            data-testid={`commissions-input-${r.operator_id}`}
                            type="number"
                            step="0.01"
                            min="0"
                            max={requesterRole === "MASTER_AGENT" ? r.parent_pct : 100}
                            value={displayValue(r)}
                            onChange={(e) => setDraft({ ...draft, [r.operator_id]: e.target.value })}
                            className="h-10 w-full rounded-md border border-slate-300 pl-3 pr-8 text-sm text-right font-semibold focus:outline-none focus:ring-2 focus:ring-[#0F62FE]"
                          />
                          <Percent className="w-3.5 h-3.5 absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                        </div>
                      </td>
                      <td className="px-5 py-3">
                        <button
                          onClick={() => save(r)}
                          data-testid={`commissions-save-${r.operator_id}`}
                          disabled={savingKey === r.operator_id || draft[r.operator_id] === undefined}
                          className="inline-flex items-center gap-1 h-9 px-4 rounded-md bg-[#0F62FE] text-white text-xs font-semibold hover:bg-[#0050E6] disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          {savingKey === r.operator_id && <Loader2 className="w-3 h-3 animate-spin" />}
                          {t("common.update")}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
